import { graphql } from 'gatsby';
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from 'gatsby-source-contentful/rich-text';
import React, { useId } from 'react';

import { FormState, useForm } from '../../../hooks/use-form';
import { RichText } from '../../rich-text/rich-text';
import Button from '../../ui/button/button';
import InputField from '../../ui/input-field/input-field';
import LinkButton from '../../ui/link-button/link-button';

export interface ContentfulBlockGiftCardOnlineWebShopProps {
  name: string;
  labelValue: string;
  defaultValue: number;
  minimumValue: number;
  maximumValue: number;
  valueValidationMessage: string;
  labelQuantity: string;
  defaultQuantity: number;
  maximumQuantity: number;
  quantityValidationMessage: string;
  buttonNext: string;
  buttonNextUrl: string;
  subtitle: string;
  body: RenderRichTextData<ContentfulRichTextGatsbyReference>;
  buttonCheckBalance: string;
  buttonCheckBalanceUrl: string;
  addDecimalsToValue: boolean;
}

const GiftCardBlock = ({
  name,
  labelValue,
  defaultValue,
  minimumValue,
  maximumValue,
  valueValidationMessage,
  labelQuantity,
  defaultQuantity,
  maximumQuantity,
  quantityValidationMessage,
  buttonNext,
  buttonNextUrl,
  subtitle,
  body,
  buttonCheckBalance,
  buttonCheckBalanceUrl,
  addDecimalsToValue,
}: ContentfulBlockGiftCardOnlineWebShopProps) => {
  const valueId = useId();
  const quantityId = useId();

  const defaultFormState: FormState = {
    value: { value: defaultValue.toString(), error: false, required: true },
    quantity: {
      value: defaultQuantity.toString(),
      error: false,
      required: true,
    },
  };

  const { onChange, values, updateInputValidityOnBlurOrFocus } =
    useForm(defaultFormState);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const target = e.currentTarget;
    if (target.checkValidity()) {
      const url =
        target.getAttribute('action') +
        '?value=' +
        `${values.value.value}${addDecimalsToValue ? '00' : ''}` +
        '&quantity=' +
        values.quantity.value;
      window.open(url, '_blank');
      addDecimalsToValue;
    }
  };

  return (
    <form
      className="text-text-default"
      method="get"
      action={buttonNextUrl}
      noValidate
      onSubmit={onSubmit}
    >
      <h2 className="mb-5 font-title text-headingm lg:mb-8 lg:text-headingxl">
        {name}
      </h2>

      <InputField
        hasError={values.value.error}
        type="number"
        id={valueId}
        label={labelValue}
        labelClassName="!text-bodym"
        errorMessage={valueValidationMessage}
        value={values.value.value}
        min={minimumValue}
        max={maximumValue}
        name="value"
        step={1}
        onChange={onChange}
        onBlur={updateInputValidityOnBlurOrFocus}
        displayWarningIconInErrorMessage={true}
        required
      ></InputField>

      <InputField
        hasError={values.quantity.error}
        type="number"
        id={quantityId}
        label={labelQuantity}
        labelClassName="!text-bodym block mt-4"
        errorMessage={quantityValidationMessage}
        value={values.quantity.value}
        min={1}
        max={maximumQuantity}
        name="quantity"
        step={1}
        onChange={onChange}
        onBlur={updateInputValidityOnBlurOrFocus}
        displayWarningIconInErrorMessage={true}
        required
      ></InputField>

      <Button type="submit" variant="primary" className="mt-8">
        {buttonNext}
      </Button>

      {subtitle && (
        <h3 className="mb-2 mt-16 font-title text-headings lg:mb-3 lg:text-headingm">
          {subtitle}
        </h3>
      )}
      {body && <RichText richText={body} />}

      {buttonCheckBalanceUrl && buttonCheckBalance && (
        <LinkButton
          to={buttonCheckBalanceUrl}
          openInNewTab
          isExternalLink
          variant="secondary"
          className="mt-8"
        >
          {buttonCheckBalance}
        </LinkButton>
      )}
    </form>
  );
};

export default GiftCardBlock;

export interface IGiftCardBlock {
  blocktype: string;
  id: string;
  name: string;
  labelValue: string;
  defaultValue: number;
  minimumValue: number;
  maximumValue: number;
  valueValidationMessage: string;
  labelQuantity: string;
  defaultQuantity: number;
  maximumQuantity: number;
  quantityValidationMessage: string;
  buttonNext: string;
  buttonNextUrl: string;
  subtitle: string;
  body: RenderRichTextData<ContentfulRichTextGatsbyReference>;
  buttonCheckBalance: string;
  buttonCheckBalanceUrl: string;
  addDecimalsToValue: boolean;
}

export const query = graphql`
  fragment BlockGiftCard on ContentfulBlockGiftCardOnlineWebShop {
    blocktype: __typename
    id
    name
    labelValue
    defaultValue
    minimumValue
    maximumValue
    valueValidationMessage
    labelQuantity
    defaultQuantity
    maximumQuantity
    quantityValidationMessage
    buttonNext
    buttonNextUrl
    subtitle
    addDecimalsToValue
    body {
      raw
    }
    buttonCheckBalance
    buttonCheckBalanceUrl
  }
`;
